import { getAccessToken, hasValidEntraAccessToken } from '@/data/api/token'

const DEFAULT_ADMIN_GROUP_IDS = ['9fce658f-b06a-464f-8965-0176280c9137']

const parseJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) {
      return null
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

const getConfiguredAdminGroupIds = (): string[] => {
  const fromEnv = import.meta.env.VITE_ENTRA_ADMIN_GROUP_IDS
  if (!fromEnv?.trim()) {
    return DEFAULT_ADMIN_GROUP_IDS
  }

  const trimmed = fromEnv.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown
      if (Array.isArray(parsed)) {
        return parsed.map((id) => String(id).toLowerCase()).filter(Boolean)
      }
    } catch {
      // fall through to comma-separated parsing
    }
  }

  return trimmed
    .split(',')
    .map((id) => id.trim().toLowerCase())
    .filter(Boolean)
}

/** Group object IDs from the Entra access token `groups` claim. */
export const getAccessTokenGroupIds = (): string[] => {
  const token = getAccessToken()
  if (!token) {
    return []
  }

  const claims = parseJwtPayload(token)
  const groups = claims?.groups
  if (!Array.isArray(groups)) {
    return []
  }

  return groups.map((group) => String(group).toLowerCase()).filter(Boolean)
}

/** True when the current access token includes a configured admin security group. */
export const hasAdminGroupAccess = (): boolean => {
  if (!hasValidEntraAccessToken()) {
    return false
  }

  const tokenGroups = new Set(getAccessTokenGroupIds())
  if (tokenGroups.size === 0) {
    return false
  }

  return getConfiguredAdminGroupIds().some((adminGroupId) => tokenGroups.has(adminGroupId))
}
